import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button, Chip } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Page, TableScroller } from '../../components/common';
import { formatDateTime } from '../../lib/format';
import { IssueList, Kpi } from '../../pages/dashboard/shared';
import { RunStatusChip, reportTitle, targetLabel, targetsOf } from '../shared';
import { billingIssues, nextSchedule, useBillingSummary } from './summary';
import '../../pages/statistics/statistics.css';
import '../../buddy/buddy.css';

const TOP = 10;
const DAY = 86_400_000;

/** Billing dashboard: schedules and their next run, recent runs, failures, and where each report can run. */
export function BillingDashboardPage() {
  const res = useBillingSummary();
  const s = res.data;
  const reports = s?.catalog.reports ?? [];
  const since = Date.now() - 30 * DAY;
  const recent = s?.runs.filter((r) => Date.parse(r.createdAt) >= since) ?? [];
  const failed = recent.filter((r) => r.status === 'failed' || r.sendError).length;
  const sent = recent.filter((r) => r.sentAt).length;
  const next = s ? nextSchedule(s) : undefined;
  return (
    <Page
      title="Billing"
      description="Reports we invoice customers from: LLM usage in NYCE tokens per customer and month for nyceBuddy and nyceBuddy3, run on demand or mailed on a schedule."
      actions={
        <Button
          size="sm"
          variant="ghost"
          icon={<RefreshCw size={14} />}
          loading={res.loading}
          onClick={res.reload}
        >
          Refresh
        </Button>
      }
    >
      <ErrorBox error={res.error} />
      {!res.enabled && <EmptyState card title="No billing access" hint="Needs ophub:billing:read." />}
      {res.enabled && !s && res.loading && <div className="oph-dash-skeleton" aria-busy="true" />}
      {s && (
        <div className={res.loading ? 'oph-dash-stale' : undefined}>
          <div className="nyk-kpis">
            <Kpi
              label="Schedules"
              value={s.schedules.filter((x) => x.enabled).length}
              sub={`${s.schedules.length} in all`}
              to="/billing/schedules"
            />
            <Kpi
              label="Next run"
              value={next ? formatDateTime(next.retryAt ?? next.nextRunAt) : '—'}
              sub={next?.name}
              to="/billing/schedules"
            />
            <Kpi label="Sent, 30 days" value={sent} to="/billing/runs" />
            <Kpi
              label="Failed, 30 days"
              value={failed}
              tone={failed ? 'red' : undefined}
              to="/billing/runs"
            />
          </div>
          <IssueList
            issues={billingIssues(s)}
            title="Billing issues"
            clear="No billing issues."
            testId="billing-issues"
          />
          <div className="bd-grid">
            <div className="nyk-card">
              <div className="nyk-card-head">
                <h2>Recent runs</h2>
                <Link to="/billing/runs" className="nyk-ui-hint">
                  History
                </Link>
              </div>
              <TableScroller>
                <table className="nyk-ui-table nyk-cards" data-testid="billing-dash-runs">
                  <thead>
                    <tr>
                      <th>Report</th>
                      <th>Period</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.runs.slice(0, TOP).map((r) => (
                      <tr key={r.id}>
                        <td className="nyk-card-title">
                          <Link to={`/billing/reports?run=${r.id}`}>{reportTitle(reports, r.reportId)}</Link>
                          <div className="nyk-ui-hint">
                            {targetLabel(reports, r.reportId, r.target)} · {formatDateTime(r.createdAt)}
                          </div>
                        </td>
                        <td data-label="Period">{r.periodLabel}</td>
                        <td data-label="Status">
                          <RunStatusChip run={r} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
              {!s.runs.length && (
                <EmptyState
                  title="No report runs yet."
                  hint={<Link to="/billing/reports">Run a report</Link>}
                />
              )}
            </div>
            <div className="nyk-card">
              <div className="nyk-card-head">
                <h2>Reports</h2>
              </div>
              <TableScroller>
                <table className="nyk-ui-table nyk-cards" data-testid="billing-dash-reports">
                  <thead>
                    <tr>
                      <th>Report</th>
                      <th>Environments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((r) => (
                      <tr key={r.id}>
                        <td className="nyk-card-title">
                          <Link to={`/billing/reports?report=${r.id}`}>{r.title}</Link>
                          <div className="nyk-ui-hint">{r.description}</div>
                        </td>
                        <td data-label="Environments">
                          <span className="nyk-chips">
                            {targetsOf(r).map((t) => (
                              <Chip
                                key={t.id}
                                size="sm"
                                className={`oph-status nyk-tone-${t.configured ? 'green' : 'grey'}`}
                                title={t.configured ? 'the hub can read it' : 'no hub credential configured'}
                              >
                                {t.label}
                              </Chip>
                            ))}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
